import { useRef, useEffect, useState, useCallback } from 'react';

export function formatPlayerTime(seconds) {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function useMediaPlayer({ onTimeUpdate, enableKeyboard = true } = {}) {
  const mediaRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Imperative methods for external control
  const seekTo = useCallback((time) => {
    if (mediaRef.current) {
      const media = mediaRef.current;
      if (media.readyState < 1) {
        const handleLoaded = () => {
          media.currentTime = time;
          media.removeEventListener('loadedmetadata', handleLoaded);
        };
        media.addEventListener('loadedmetadata', handleLoaded);
      } else {
        media.currentTime = time;
      }
    }
  }, []);

  const play = useCallback(() => {
    if (mediaRef.current) mediaRef.current.play();
  }, []);

  const pause = useCallback(() => {
    if (mediaRef.current) mediaRef.current.pause();
  }, []);

  const togglePlay = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    if (isPlaying) {
      media.pause();
    } else {
      media.play();
    }
  }, [isPlaying]);

  // Media event listeners
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const handleTime = () => {
      setCurrentTime(media.currentTime);
      if (onTimeUpdate) onTimeUpdate(media.currentTime);
    };

    const handleLoadedMetadata = () => setDuration(media.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    media.addEventListener('timeupdate', handleTime);
    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('play', handlePlay);
    media.addEventListener('pause', handlePause);

    return () => {
      media.removeEventListener('timeupdate', handleTime);
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('play', handlePlay);
      media.removeEventListener('pause', handlePause);
    };
  }, [onTimeUpdate]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!enableKeyboard) return;

    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const media = mediaRef.current;
      if (!media) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (isPlaying) media.pause();
          else media.play();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          media.currentTime = Math.max(0, media.currentTime - 5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          media.currentTime = Math.min(duration, media.currentTime + 5);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume((v) => {
            const newVol = Math.min(1, v + 0.1);
            media.volume = newVol;
            return newVol;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume((v) => {
            const newVol = Math.max(0, v - 0.1);
            media.volume = newVol;
            return newVol;
          });
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboard, isPlaying, duration]);

  const handleSeek = useCallback((e) => {
    const media = mediaRef.current;
    if (!media) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    media.currentTime = percent * duration;
  }, [duration]);

  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (mediaRef.current) mediaRef.current.volume = newVolume;
  }, []);

  const handleRateChange = useCallback((rate) => {
    setPlaybackRate(rate);
    if (mediaRef.current) mediaRef.current.playbackRate = rate;
  }, []);

  return {
    mediaRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    seekTo,
    play,
    pause,
    togglePlay,
    handleSeek,
    handleVolumeChange,
    handleRateChange,
  };
}
